// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Sample, SampleCreate, SampleResult } from '../../api/types';
import { parseMiljoeQuery } from '../../app/links';
import { createOnceGuard } from '../../app/onceGuard';
import { sampleToken } from '../../bygning/lens';
import { format, type Selection } from '../../bygning/selection';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Pill } from '../../components/Pill';
import { Spinner } from '../../components/Spinner';
import { Strip } from '../../components/bygning/Strip';
import { StripCard } from '../../components/bygning/StripCard';
import { SampleInspector } from '../../components/bygning/miljoe/SampleInspector';
import { firstPlus, linkedTypeText, sampleRooms } from '../../components/bygning/miljoe/sampleInfo';
import { NewSampleForm } from '../../components/miljoe/NewSampleForm';
import {
  advancePatch,
  linksOf,
  resultPatch,
  resultToast,
  statusPill,
  toggleLink,
  togglePart,
  UNDO_RESULT_PATCH,
  type SampleLinks,
} from '../../miljoe/model';
import type { ModeComponent } from './modeTypes';
import styles from './MiljoeMode.module.css';

/**
 * Miljø & prøver (PLAN.md layout contract): no left tile (the sample-stage
 * legend floats over the model: the Ground draws it), the sample inspector
 * on the right (`?sample=`, else the first sample), the samples in the strip
 * with a "+ Ny prøve" card. Replaces
 * MiljoePage behaviour for behaviour: every write runs on the case store's
 * queue, which re-reads the survey after it and toasts what the change did
 * to the approval gate (`gateMessage`), else the fallback toast below.
 * `?ny=<typeId>` opens the create form with that type pre-linked (the
 * convention Ressourcer's "Opret prøve" links to); the query is dropped once
 * the form is done.
 */
export const MiljoeMode: ModeComponent = ({ data, sel, setSel, actions }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = parseMiljoeQuery(location.search);

  // Queued writes outlive the render that queued them: read through refs.
  const locationRef = useRef(location);
  locationRef.current = location;
  const selRef = useRef(sel);
  selRef.current = sel;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [localCreating, setLocalCreating] = useState(false);
  const creating = data.canEdit && (localCreating || query.newForType !== null);
  // `busy` only disables the button on the next render: the guard shuts at once.
  const [createInFlight] = useState(createOnceGuard);

  // Link toggles are field commits: each sends the full desired set — whole
  // types and single parts (PUT, the last request wins) — and the picker
  // shows that set until the last queued PUT for the sample has settled
  // (MiljoePage's rule).
  const [linkDrafts, setLinkDrafts] = useState<ReadonlyMap<number, SampleLinks>>(() => new Map());
  const linkDraftsRef = useRef<ReadonlyMap<number, SampleLinks>>(new Map());
  const linkSeq = useRef(new Map<number, number>());
  const setLinkDraft = useCallback((id: number, links: SampleLinks | null) => {
    const next = new Map(linkDraftsRef.current);
    if (links === null) next.delete(id);
    else next.set(id, links);
    linkDraftsRef.current = next;
    setLinkDrafts(next);
  }, []);

  const samples = data.samples;
  const sample = samples.find((s) => s.id === sel.sampleId) ?? samples[0] ?? null;

  /** The query for `patch` without `?ny=` (the form it opened is done). */
  function searchWithoutNew(patch: Partial<Selection>): string {
    const keep = new URLSearchParams(locationRef.current.search);
    keep.delete('ny');
    const q = format({ ...selRef.current, ...patch }, keep);
    return q ? `?${q}` : '';
  }

  function closeForm(focus: number | null) {
    setLocalCreating(false);
    // Off the mode (navigated away mid-create), the user's own history entry stays.
    if (!mountedRef.current) return;
    const hadQuery = parseMiljoeQuery(locationRef.current.search).newForType !== null;
    if (focus !== null) navigate({ pathname: locationRef.current.pathname, search: searchWithoutNew({ sampleId: focus }) });
    else if (hadQuery) navigate({ pathname: locationRef.current.pathname, search: searchWithoutNew({}) }, { replace: true });
  }

  function create(body: SampleCreate) {
    if (data.busy) return;
    createInFlight.run(() =>
      actions.createSample(body).then((created) => {
        // A failure has toasted; the form stays open with what was typed.
        if (created) closeForm(created.id);
      }),
    );
  }

  // Buttons: gated on `busy`, so a double click cannot stack requests.
  function advance(s: Sample) {
    const body = advancePatch(s);
    if (data.busy || !body) return;
    void actions.patchSample(s.id, body);
  }

  function recordResult(s: Sample, result: SampleResult) {
    if (data.busy) return;
    void actions.patchSample(s.id, resultPatch(result), {
      fallbackToast: resultToast(s.code, result),
      onUnprocessable: () => actions.notify(`${s.code}: svaret kan først registreres, når prøven er sendt til lab.`),
    });
  }

  function undoResult(s: Sample) {
    if (data.busy) return;
    void actions.patchSample(s.id, UNDO_RESULT_PATCH, {
      fallbackToast: `${s.code}: svaret er fortrudt — afventer igen svar fra lab`,
    });
  }

  function remove(s: Sample) {
    if (data.busy) return;
    const at = samples.findIndex((x) => x.id === s.id);
    const neighbour = at < 0 ? undefined : (samples[at + 1] ?? samples[at - 1]);
    void actions.deleteSample(s.id).then((ok) => {
      if (ok && mountedRef.current && selRef.current.mode === 'miljoe') setSel({ sampleId: neighbour?.id ?? null });
    });
  }

  const linksShown = (s: Sample): SampleLinks => linkDrafts.get(s.id) ?? linksOf(s);

  function commitLinks(s: Sample, change: (base: SampleLinks) => SampleLinks) {
    const next = change(linkDraftsRef.current.get(s.id) ?? linksOf(s));
    setLinkDraft(s.id, next);
    const seq = (linkSeq.current.get(s.id) ?? 0) + 1;
    linkSeq.current.set(s.id, seq);
    void actions.setSampleLinks(s.id, next).finally(() => {
      // Only the last toggle hands the picker back to the server's set.
      if (linkSeq.current.get(s.id) === seq) setLinkDraft(s.id, null);
    });
  }

  const toggle = (s: Sample, typeId: number) =>
    commitLinks(s, (base) => ({ type_ids: toggleLink(base.type_ids, typeId), part_codes: base.part_codes }));
  const togglePartLink = (s: Sample, code: string) =>
    commitLinks(s, (base) => ({ type_ids: base.type_ids, part_codes: togglePart(base.part_codes, code) }));

  function openCreate() {
    setLocalCreating(true);
  }

  // ------------------------------------------------------------- slots --

  // An unknown or rejected `?ny=` type opens an empty form.
  const preLinked =
    query.newForType !== null && data.types.some((t) => t.id === query.newForType && t.review_status !== 'rejected')
      ? [query.newForType]
      : [];

  let right: ReactNode;
  if (!data.loaded) {
    right = <Spinner label="Indlæser prøver…" />;
  } else if (creating) {
    right = (
      <div className={styles.form}>
        <NewSampleForm
          key={query.newForType ?? 'ny'}
          types={data.types}
          initialTypeIds={preLinked}
          busy={data.busy}
          onSubmit={create}
          onCancel={() => closeForm(null)}
        />
      </div>
    );
  } else if (!sample) {
    right = (
      <EmptyState
        bare
        title="Ingen prøver endnu"
        detail="Registrér en miljøprøve og kobl den til de typer eller enkelte dele i kortlægningen, den dækker. Indtil svaret foreligger, kan typerne ikke godkendes."
        action={
          data.canEdit ? (
            <Button variant="primary" size="lg" onClick={openCreate}>
              + Ny prøve
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    right = (
      <SampleInspector
        key={sample.id}
        sample={sample}
        types={data.types}
        links={linksShown(sample)}
        busy={data.busy}
        onAdvance={() => advance(sample)}
        onResult={(r) => recordResult(sample, r)}
        onUndoResult={() => undoResult(sample)}
        onTitle={(title) => void actions.patchSample(sample.id, { title })}
        onWhat={(what) => void actions.patchSample(sample.id, { what })}
        onToggleLink={(typeId) => toggle(sample, typeId)}
        onTogglePart={(code) => togglePartLink(sample, code)}
        onDelete={() => remove(sample)}
        onOpenType={(typeId, partCode) => setSel({ mode: 'ressourcer', typeId, partCode })}
      />
    );
  }

  const bottom = data.loaded ? (
    <Strip label="Prøver" activeKey={sample?.id ?? null}>
      {samples.map((s) => {
        const ids = linksShown(s);
        const room = firstPlus(sampleRooms(ids, data.types));
        const type = linkedTypeText(ids, data.types);
        const pill = statusPill(s);
        return (
          <StripCard
            key={s.id}
            variant="sample"
            title={`${s.code} · ${s.title}`}
            swatch={sampleToken(s)}
            figure={room ?? 'Uden rum'}
            figureMuted={room === null}
            meta={type ?? 'Ingen type'}
            status={<Pill tone={pill.tone}>{pill.label}</Pill>}
            active={!creating && sample?.id === s.id}
            onClick={() => {
              setLocalCreating(false);
              // Leaving an open `?ny=` form drops the query with it.
              if (query.newForType !== null) navigate({ pathname: location.pathname, search: searchWithoutNew({ sampleId: s.id }) });
              else setSel({ sampleId: s.id });
            }}
          />
        );
      })}
      {data.canEdit && <StripCard variant="dashed" title="+ Ny prøve" disabled={creating} onClick={openCreate} />}
      {!data.canEdit && samples.length === 0 && <p className={styles.stripEmpty}>Ingen prøver endnu.</p>}
    </Strip>
  ) : undefined;

  return { right, bottom };
};
